import { use } from 'react'

import { useNavigate } from 'react-router-dom'

import {
  fetchConversation,
  fetchConversationMessages,
  joinChat,
  readChat,
} from '@app/api'
import { ChatContext } from '@app/context'
import { getContextEntity } from '@app/helpers'
import { useConversation, useConversationActorRole } from '@app/hooks'
import { Message } from '@app/models'
import { trackConversationJoin } from '@app/services/tracking'
import {
  closeConversationAndNavigateToMessages,
  handleForbiddenConversationError,
} from '@app/utils'

export const useJoin = () => {
  const navigate = useNavigate()
  const conversation = useConversation()
  const conversationActorRole = useConversationActorRole()
  const { dispatch, state } = use(ChatContext)
  const handleJoinConversation = async () => {
    if (!conversation) {
      return
    }

    dispatch({
      type: 'JOIN_CONVERSATION_START',
      payload: conversation.id,
    })

    try {
      const participant = conversation?.participants.find(
        (p) => p.acsChatParticipantId === state.acsChatParticipantId
      )
      if (participant?.joinedAcs) {
        return
      }

      const { acsChatThreadId } = await joinChat(
        state.acsChatParticipantId,
        state.acsChatToken.token,
        conversation.id
      )

      if (acsChatThreadId && participant) {
        participant.joinedAcs = true
      }

      await readChat(
        state.acsChatParticipantId,
        state.acsChatToken.token,
        conversation.id
      )

      const { participants, updateDate } = await fetchConversation(
        conversation.id
      )

      let messages: Message[] = []

      const result = await fetchConversationMessages(
        conversation.id,
        null,
        1,
        new AbortController().signal
      )

      if (
        result.messages.length > 0 &&
        result.total > conversation.messages.length
      ) {
        messages = result.messages

        const latestMessage = result.messages[0]
        const expectedMessagesLength =
          conversation.messages.length + result.messages.length

        if (expectedMessagesLength < result.total) {
          const updatedResult = await fetchConversationMessages(
            conversation.id,
            latestMessage.id,
            result.total - expectedMessagesLength,
            new AbortController().signal
          )

          messages = [...updatedResult.messages.reverse(), ...messages]
        }
      }

      const listing = getContextEntity(state.listings, conversation)
      trackConversationJoin(conversation.id, listing, conversationActorRole)

      dispatch({
        type: 'JOIN_CONVERSATION_SUCCESS',
        payload: {
          acsChatThreadId,
          participants,
          id: conversation.id,
          messages: messages.reverse(),
          total: result.total,
          updateDate,
        },
      })
    } catch (error) {
      if (
        handleForbiddenConversationError(error, () => {
          closeConversationAndNavigateToMessages(navigate)
        })
      ) {
        return
      }

      dispatch({
        type: 'JOIN_CONVERSATION_ERROR',
        payload: conversation.id,
      })
    }
  }

  return {
    disabled: conversation?.joining === 'PENDING',
    handleJoinConversation,
  }
}
